import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  catchError,
  finalize,
  map,
  Observable,
  of,
} from "rxjs";
import { AppLoadError, ILanguage } from "../shared/models";
import { LanguageListService } from "../shared/services/api/language-list.service";

@Injectable({
  providedIn: "root",
})
export class LanguageListStoreService {
  private _languageList: BehaviorSubject<ILanguage[] | null> =
    new BehaviorSubject<ILanguage[] | null>(null);
  private _loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  private _error: BehaviorSubject<AppLoadError | null> =
    new BehaviorSubject<AppLoadError | null>(null);

  public languageList$: Observable<ILanguage[]> =
    this._languageList.asObservable();

  public loading$: Observable<boolean> = this._loading.asObservable();
  public error$: Observable<AppLoadError | null> = this._error.asObservable();

  get languageList() {
    return this._languageList.getValue();
  }

  constructor(private languageListService: LanguageListService) {}

  selectLanguageList(): Observable<ILanguage[]> {
    return this.languageList$.pipe(map((languageList) => languageList));
  }

  // Select the loading state
  selectLoading(): Observable<boolean> {
    return this.loading$.pipe(map((loading) => loading));
  }

  // Select the error state
  selectError(): Observable<AppLoadError | null> {
    return this.error$.pipe(map((error) => error));
  }

  /**
   * Load language list from the API or from local cache.
   * @GET
   */
  loadLanguageList(): Observable<ILanguage[]> {
    this._loading.next(true);
    return this.languageListService.getLanguageList().pipe(
      map((languageList) => {
        this._languageList.next(languageList);
        return languageList;
      }),
      catchError((error) => {
        const displayError =
          error instanceof AppLoadError ? error : new AppLoadError();
        this._error.next(displayError);
        this._loading.next(false);
        return of(null);
      }),
      finalize(() => this._loading.next(false))
    );
  }
}
